import { LitElement, html, customElement, css, property, TemplateResult,  } from 'lit-element';
import { nothing, PropertyValues } from 'lit';
import { TerminalsModel } from '../../../src/models/terminals-model';
import { VesselsTrackingModel } from '../../../src/models/vessels-tracking-model';
import { VoyageSchedulesModel } from '../../../src/models/voyage-schedules-model';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { VoyWeatherBroadcastDrawerMetaData } from './voy-weather-broadcast-drawer.types';
import { voyageCursorStyles } from '../../voyage-style/src/voyage-style';
import { VoyWeatherBroadcastManager } from './voy-weather-brodcast-manager';
import { WeatherBroadcast } from './types';
import { toTitleCase } from '../../../src/utils/utils';
import { CoordinatesUtils } from '../../../src/utils/coordinates-utils';
import '../../voyage-checkbox/src/voyage-checkbox';
import "@maersk-global/mds-components-core/mc-checkbox";
import "@maersk-global/mds-components-core/mc-button";
import "@maersk-global/mds-components-core/mc-notification";
import { AuthorizationRoles, AuthService } from '../../../src/services/auth-service';
import { VoyGlobalFilters } from '../../voy-global-filters/src/voy-global-filters';
import { FilterType, VoyageFilterResult } from '../../voy-global-filters/src/types';
import { FiSubView, UiStateModel, UiStateModelParam } from '../../../src/models/ui-state-model';
import { FilterUtils } from '../../voy-global-filters/src/filter-utils';
import { UserConfigService } from '../../../src/services/user-config-services';
import { FilterEvents } from '../../../src/models/types/voy-fleet-alarm-model.types';

@customElement('voy-weather-broadcast-drawer')
export class VoyWeatherBroadcastDrawer extends LitElement {
    @property({ type: Boolean, reflect: true }) shown: boolean = false;
    @property({ type: Boolean, attribute: false }) private _maximized: boolean = false;
    @property({ type: Boolean, reflect: true }) private _selection: Map<number, VoyWeatherBroadcastDrawerMetaData> = new Map();
    @property({ type: Boolean, reflect: true }) private _copyClicked: boolean = false;
    @property({ type: Boolean, reflect: true }) private _allSelectChecked: boolean = true;
    @property({ type: Boolean, reflect: true }) private _onClose: boolean = false;
    @property({ attribute: false }) private _filterText: string = '';
    @property({ attribute: false }) private _commands: VoyageFilterResult[] = [];
    @property({ attribute: false }) private isFilterOpen: boolean = false;
    @property({ type: Boolean}) public _isUserInternal: boolean = AuthService.userHasRole(AuthorizationRoles.MaerskInternal);
    @property({ attribute: false }) private _savedFilters:[] = [];
    @property({ attribute: false }) private _filterApplied: boolean = false;
    private previousFilter: string = '';

    constructor() {
        super();
        
        this.close = this.close.bind(this);
        this._onSelectionChanged = this._onSelectionChanged.bind(this);
        this._saveFilters = this._saveFilters.bind(this);
    }

    static get styles() {
        return [
            voyageCursorStyles,
            css`
                :host {
                    display: block;
                    position: absolute;
                    right: -4px;
                    top: 59px;
                    box-sizing: border-box;
                    transform: translateX(100%);
                    background: white;
                    transition: transform 250ms ease-out;
                    box-shadow: 1px 1px 5px var(--secondary-voyage-box-shadow);
                    border: 1px solid var(--neutrals-grey-06, black);
                    border-radius: 4px;
                    font-family: MaerskText;
                    max-height: calc(100vh - 216px);
                    width: 300px;
                    font-size: 12px;
                }

                :host([shown]) {
                    transform: translateX(0);
                    background: white;
                    margin-right: 20px;
                }

                :host([transparent]) {
                    opacity: 0.5;
                }

                :host(:hover) {
                    opacity: 1;
                }

                #topbar {
                    width: 100%;
                    touch-action: none;
                    display: grid;
                    grid-template-columns: 1fr auto;
                    align-items: center;
                    border-bottom: 1px solid var(--neutrals-grey-07, grey);
                    box-sizing: border-box;
                    user-select: none;
                }

                .expand-container {
                    position: absolute;
                    margin-left: 38px;
                    margin-top: 0px;
                    height: 20px;
                    width: 20px;
                    z-index: 10;
                    cursor: pointer;
                    pointer-events: auto;
                }

                .expand-container > svg {
                    fill: var(--primary-blue-01, black);
                    height: 100%;
                    width: 100%;
                }

                .drawerTitle {
                    grid-column: 1;
                    font-family: MaerskHeadline;
                    font-size: 20px;
                    font-weight: 400;
                    line-height: 24px;
                    letter-spacing: 0em;
                    text-align: left;
                    padding: 16px;
                }

                .close {
                    grid-column: 2;
                    background: none;
                    border: none;
                    outline: none;
                    cursor: pointer;
                    padding: 16px;
                    fill: var(--neutrals-grey-02, black);
                }

                .close > svg {
                    display: block;
                    width: 13px;
                }

                .title {
                    display: flex;
                    align-items: center;
                    font-weight: bold;
                }

                .name-container {
                    display: flex;
                }

                .row-border {
                    border-top: 1px solid var(--neutrals-grey-07, grey);
                    grid-column: 1 / 7; /* this code makes the row stretch to entire width of the container */
                }

                .checkbox-container {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }
                
                .next-port,
                .heading,
                .speed {
                    color: var(--functional-grey-600, grey);
                }

                .email {
                    width: 150px; 
                    white-space: nowrap; 
                    overflow: hidden; 
                    text-overflow: ellipsis;
                    color: var(--functional-grey-600, grey); 
                }

                .copy-click-confirmation-container {
                    display: none;
                    align-items: center;
                    margin-bottom: 16px;
                    box-shadow: 0px 1px 8px rgba(0, 0, 0, 0.04), 0px 3px 3px -2px rgba(0, 0, 0, 0.03), 0px 3px 4px rgba(0, 0, 0, 0.03);
                    border-radius: 5px;
                    font-size: 14px;
                    color: var(--functional-grey-900, black);
                }

                .copy-click-confirmation-container[show] {
                    display: flex;
                }

                .copy-click-confirmation-container mc-notification {
                    width: 100%;
                }

                .copy-confirmation-icon {
                    display: flex;
                }
                .copy-confirmation-icon svg {
                    fill: var(--functional-grey-900, black);
                    width: 17px;
                    height: 17px;
                    margin-right: 12px;
                    margin-top: 5px;
                }
                .content {
                    display: flex;
                    flex-direction: column;
                    max-height: calc(100vh - 223px);
                    background-color:white;
                }

                .container {
                    font-size: 16px;
                    padding: 16px 16px;
                    flex-grow: 1;
                    overflow-y: auto;
                }

                .table {
                    display: grid;
                    grid-template-columns:  120px 150px 100px 80px 60px;
                    grid-gap: 10px;
                    font-size: 12px;
                }

                .table[growingContainer] {
                    display: grid;
                    grid-template-columns: 120px 150px 100px 80px 60px;
                    grid-gap: 10px;
                    font-size: 12px;
                    height: 100%;
                }

                .copy-btn-container {
                    display: flex;
                    align-items: center;
                    justify-content: flex-start;
                    gap: 16px;
                    min-height: 64px;
                    padding-right: 16px;
                    padding-left: 16px;
                    background-color: #fff;
                    border-top: 1px solid var(--neutrals-grey-07, grey);
                    box-shadow: 0px -2px 4px 2px rgba(0, 0, 0, 0.05), 0px 3px 1px -2px rgba(0, 0, 0, 0.03), 0px 2px 2px rgba(0, 0, 0, 0.03);
                }
                .vessel-title {
                    color: var(--functional-grey-900, black);
                    font-size: 12px;
                    font-family: Maersk Text;
                    font-weight: 400;
                    line-height: 16px;
                    letter-spacing: 0em;
                    text-align: left;

                }

                mc-checkbox::part(checkmark) {
                     background-color: var(--primary-blue-05, blue);
                     border-color: var(--primary-blue-05, blue);
                }
                
                .input-icon-container {
                    padding: 10px;
                    position: relative;
                    display: flex;
                    align-items: center;
                    flex-grow: 1;
                }
                
                .filter{
                    background: none;
                    border: none;
                    outline: none;
                    cursor: pointer;
                    padding: 16px;
                }
                
                .action-buttons {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }

                .create-filter {
                    width: 57%;
                    padding: 10px 8px;
                    box-sizing: border-box;
                    border: 1px solid var(--neutrals-grey-06, grey);
                    border-radius: 5px;
                    font-size: 12px;
                }
                
                create-filter::placeholder {
                    color: var(--neutrals-grey-05, grey);
                }
                
                .save-filter {
                    margin-left: 10px;
                }
                .list-with-iocn{
                    display: flex;
                    align-items: flex-end;
                    gap: 8px;
                }
            `
        ]   
    };

    updated(changedProperties: PropertyValues) {
        if (changedProperties.has("shown")) {
            if (this.shown) {
                this.getSavedFilters();
                this.previousFilter = UiStateModel!.getValue('q') || '';
                this._commands = [];
            } 
        }
    }

    render() {
        const showCopyButton = this._selection.size > 0;
        return html`
        <div class="wrapper">
            <div id="topbar">
                <div class="drawerTitle list-with-iocn">Vessel list
                <mc-icon style="cursor:pointer" icon = "square-arrow-up-right"  @click=${this._exportCSV}></mc-icon>
                </div>
                <div class="action-buttons">
                    <button class="filter" @click="${this.openFilter}">${!this.isFilterOpen ? SvgIcons.customFilterSliders : SvgIcons.reset}</button>
                    <button class="close" @pointerdown=${this.close}>${SvgIcons.lightTimes}</button>
                    <div class="expand-container" title="${this._maximized ? 'Minimize Vessel List' : 'Maximize Vessel List'}" @click=${this._setMaximized}>
                        ${this._maximized ? SvgIcons.shrink : SvgIcons.expand}
                    </div>
                </div>
            </div>
            <div class="content">
                
                <div class="container">
                    <div class="copy-click-confirmation-container" ?show=${this._copyClicked}>
                        <mc-notification
                            body="The list of emails/names has been copied"
                            appearance="success"
                            fit="small"
                            icon="check-circle"
                        ></mc-notification>
                    </div>
                    ${this._renderList()}
                </div>

             
            ${showCopyButton ? html`  
            <div class="copy-btn-container">
            <mc-button variant="filled" appearance="primary" label="Copy E-mails" @click=${()=>this._onCopy("emails")} fit="small" type="button"></mc-button>
            <mc-button variant="filled" appearance="primary" label="Copy Names" @click=${()=>this._onCopy("names")} fit="small" type="button"></mc-button>

            </div> 
            <div>
            ${this.isFilterOpen ? html`
                <div class="input-icon-container">
                        <input 
                            type="text" 
                            class="create-filter" 
                            ?error=${!this._filterText} 
                            placeholder="Enter filter name here" 
                            @input=${this.onChangeFilterText} 
                            .value=${this._filterText} 
                        />
                        <div class="save-filter">
                            <mc-button variant="filled" appearance="primary" label="Save as filter" @click=${this._saveFilters} fit="small" type="button"></mc-button>
                        </div>
                </div>
            `: nothing}
            </div>
            `: nothing}
            </div>
        </div>
        `;
    }

    private loadPreviousFilter() {
        const previousFilter = FilterUtils.stringToFilter(this.previousFilter);

        this._commands = [];
        if (!previousFilter?.commands.length) return;

        this._commands = previousFilter.commands;
        UiStateModel!.setLeftPanelVisibility(true, "hide-anchor");
        UiStateModel!.setShowAnchor(true);
        UiStateModel!.setValue(UiStateModelParam.FISubView, FiSubView.ListView);
    }

    private openFilter() {
        if (this.isFilterOpen) {
            // Close filter panel and restore the previous state
            this.loadPreviousFilter();
            this.filterResult = '';
            UiStateModel!.setValue('q', this.previousFilter);
            this._filterApplied = false;
        } else {
            // Open filter panel
            const newCmd = { cmd: FilterType.ImoGrouping, vals: this.keyArray };
            this._commands.push(newCmd);
            UiStateModel!.setLeftPanelVisibility(true, "hide-anchor");
            UiStateModel!.setShowAnchor(true);
            this.filterResult = FilterUtils.filterToString({ commands: this._commands });
            UiStateModel!.setValue('q', this.filterResult);
            UiStateModel!.setValue(UiStateModelParam.FISubView, FiSubView.ListView);
            this._filterApplied = true;
        }
          
        this.isFilterOpen = !this.isFilterOpen;
        this.requestUpdate(); 
    }

    private onChangeFilterText(event: {target: {value: string}}) {  
        this._filterText = event!.target!.value;
      }

    private async _saveFilters(){
        if(this._filterText && this.filterResult){
            const user = AuthService.getCurrentUserId()
            let payload = this.constructPayload(user,this.filterResult)
            let filterNameExists: any = this._savedFilters.find((item: any) => item.filterName === this._filterText);

            if(filterNameExists){
                this.dispatchEvent(new CustomEvent(FilterEvents.FilterSaved, {
                    detail: {
                      message: `Filter name already exists. Please choose a different name.`,
                      type: 'error'
                    },
                    bubbles: true,
                    composed: true
                  }));  
                return;
            }

            try{
                await UserConfigService.upsertFilter(payload);
                this.dispatchEvent(new CustomEvent(FilterEvents.FilterSaved, {
                    detail: {
                      message: `${this._filterText} has been created.`,
                      type: 'success'
                    },
                    bubbles: true,
                    composed: true
                  }));  
            } catch (err) { 
                console.error(err);
            }
        }
    }

    connectedCallback(): void {
        super.connectedCallback();
        VoyWeatherBroadcastManager.addEventListener(WeatherBroadcast.SelectionUpdated, <EventListener>(this._onSelectionChanged));
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        VoyWeatherBroadcastManager.removeEventListener(WeatherBroadcast.SelectionUpdated, <EventListener>(this._onSelectionChanged));
    }
    private _exportCSV() {
        if (!this._selection.size) return;
        const keys: string[]  = [ "Vessel Name", "Imo", "Email", "Heading", "Next Port", "Speed",]

         const csvRows: string[][] = [];
         csvRows.push(keys)
        this._selection.forEach(item => {
            const rowData: string[] = [];
            if(item?.includedForClipboard === true){
                rowData.push(item["vesselName"]?.toString() ?? "");
                rowData.push(item["imo"]?.toString() ?? "");
                rowData.push(item["email"] ?? "");
                rowData.push(item["heading"] ? item["heading"]+ "º" + " " + "(" + CoordinatesUtils.getCardinalDirection(item["heading"]!) + ")"  : "");
                rowData.push(item["nextPort"]?.toString() ?? "");
                rowData.push(item["speed"]?.toString() + ` Kt`);
            }
            
            csvRows.push(rowData);
        })
        let csvContent = ''

        csvRows.forEach(row => {
            csvContent += row.join(',') + '\n'
        })
        const bom = '\uFEFF';
        csvContent = bom + csvContent;
        const csvData = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const csvUrl = URL.createObjectURL(csvData);
        const hiddenElement = document.createElement('a');
        hiddenElement.href = csvUrl;
        hiddenElement.download = 'VesselList.csv';
        hiddenElement.click();
    }


    private _onCopy(type: string) {
        if (!this._selection.size) return;
        let recipientFieldText = "";
        for (const [imo, vessel] of Array.from(this._selection)) {
            if (vessel?.includedForClipboard === true) {
                if(type === "emails") {
                if (vessel.email) recipientFieldText += `${vessel.email};`;
                }
                else if (type === "names") {
                 if (vessel.vesselName) recipientFieldText += `${vessel.vesselName},`;
                }
                
            }
        }

        this._copyTextToClipboard(recipientFieldText);
        this._copyClicked = true;
    }

    private _copyTextToClipboard(text: string) {
        if (!navigator.clipboard) {
          this._fallbackCopyTextToClipboard(text);
          return;
        }
        navigator.clipboard.writeText(text).then(function() {
          console.log('Copying vessel names to clipboard was successful!');
        }, function(err) {
          console.error('Could not copy text: ', err);
        });
    }

    private _fallbackCopyTextToClipboard(text: string) {
        const temp = document.createElement('input');
        temp.value = text;
        document.body.appendChild(temp);
        temp.select();
        document.execCommand('copy');
        document.body.removeChild(temp);
    }

    private _onSelectionChanged(e: CustomEvent) {
        this._onClose = false;
        const {deselectedImos, selectedImos} = e.detail;
        for (const imo of deselectedImos) {
            this._selection.delete(imo);
        }
        this._copyClicked = false;
        this.requestUpdate();
        
        this._updateCollectionMetaData(selectedImos);
        this._copyClicked = false;
    }

    private async _updateCollectionMetaData(imos: number[]) {
        const promises = [];
        for (const imo of imos) {
            promises.push(this._createMetaData(imo));

            const metaDatas = await Promise.all(promises);
            for (const data of metaDatas) {
                if(this._onClose === false){
                    this._selection.set(imo, data)
                }else{
                    this._selection.clear()
                    break;
                }
            }
            if (this._onClose === true) {
                break; 
            }
            this._selection = this._sortSelectionByPort();
            this.requestUpdate(); //TODO: can this be faster. It's getting one port at a time
        }
    }

    private _sortSelectionByPort() {
        const sortedSelection = Array.from(this._selection).sort((a, b) => {
            // '\uffff' represents a very high Unicode character. This ensures that null values are
            // sorted last because '\uffff' has a higher Unicode value than any other character.
            const nextPortA = a[1].nextPort !== null ? a[1].nextPort.toUpperCase() : "\uffff";
            const nextPortB = b[1].nextPort !== null ? b[1].nextPort.toUpperCase() : "\uffff";
            
            if (nextPortA < nextPortB) {
                return -1;
            }
            if (nextPortA > nextPortB) {
                return 1;
            }
            return 0;
        });

          return new Map(sortedSelection);
    }

    private async _createMetaData(imo: number): Promise<VoyWeatherBroadcastDrawerMetaData> {
        const position = VesselsTrackingModel!.allVesselPositions.get(imo);
        const nextPort = await this._getNextPort(imo);
        const email = VesselsTrackingModel!.maerskVesselEmail(imo);
        return {
            imo,
            vesselName: position!.vesselName,
            email: email ? email : "",
            nextPort: nextPort ? nextPort : null,
            heading: position!.heading,
            speed: position!.sog,
            includedForClipboard: true
        }
    }

    private async _getNextPort(imo: number) {
        const schedules = await VoyageSchedulesModel?.getScheduleFor(imo);
        const schedule = schedules?.find(schedule => schedule.active);

        if (!schedule) return;

        const arrivalTerminal = TerminalsModel!.getTerminal(schedule.arrivalTerminalCode)?.city;

        return arrivalTerminal;
    }

    @property({ attribute: false }) filterResult?: string;
    @property({ attribute: false }) keyArray?: string[];
    private _renderList(): TemplateResult {
        if (!this._selection.size) {
            return html`Please drag the mouse on the map to select vessels`
        }

        const numberOfActiveFilters = this._commands.reduce((prev, curr) => {
            if (curr!.vals!.includes('maersk')) {
                return prev + curr!.vals!.length - 1 ;
            } else if (curr!.cmd === 'tml') {
                return prev;
            }
            return prev + curr!.vals!.length
       }, 0);
       VoyGlobalFilters.isFilterApplied = numberOfActiveFilters > 0;
       this.keyArray = Array.from(this._selection.keys()).map(key => key.toString());

        return html`

            <div class="table" ?growingContainer=${this._copyClicked}>
                <div class="title">${this._renderToggleAll()} Vessel name</div>
                <div class="title">Email</div>
                <div class="title">Next port</div>
                <div class="title">Heading</div>
                <div class="title">Speed</div>

                ${Array.from(this._selection).map((vessel) => {
                    const metaData = vessel[1];
                    return this._renderEntry(metaData);
                })}
            </div> 
         `
    }

    private _renderToggleAll(): TemplateResult {
        return html`
            <mc-checkbox
                id="Toggle All"
                name="disclaimer"
                @change=${this._onToggleAll}
                .checked=${this._allSelectChecked}
                fit="small"
                label=""
                >
            </mc-checkbox>
        `
    }

    private _renderEntry(vessel: VoyWeatherBroadcastDrawerMetaData) {
        return html`
            <div class="row-border"></div>
                <div class="name-container">
                    <div class="checkbox-container">
                        <mc-checkbox
                            id=${vessel.vesselName}
                            name="disclaimer"
                            @change=${() => this._onCheck(vessel.imo)}
                            .checked=${vessel.includedForClipboard}
                            fit="small"
                            >
                                <span slot="label"><div class="vessel-title">${toTitleCase(vessel.vesselName)}</div></span>
                        </mc-checkbox>
                    </div>
                </div>
                <div class="email" >${vessel.email}</div>
            <div class="next-port">${vessel.nextPort}</div>
            <div class="heading">${vessel.heading}º
            (${CoordinatesUtils.getCardinalDirection(vessel.heading!)})
            </div>
            <div class="speed">${vessel.speed} ${vessel.speed ?`Kt`:""}</div>
        `
    }

    private _onCheck(imo:number) {
        const vessel = this._selection.get(imo);
        if (!vessel) return;
        vessel.includedForClipboard = !vessel.includedForClipboard;
        this._copyClicked = false;
    }

    private _onToggleAll() {
        this._allSelectChecked = !this._allSelectChecked;
        
        for (const [_, vessel] of this._selection) {
            vessel.includedForClipboard = this._allSelectChecked;
        }
        this._copyClicked = false;
    }

    private async _setMaximized() {
        this._maximized = !this._maximized;

        if (this._maximized) {
            this.style.setProperty('width', '437px');

        } else {
            this.style.setProperty('width', '300px');
        }
    }

    constructPayload(user: any, filters: string){
        let payload = {
            id: 0,
            data:  JSON.stringify({filters: filters}),
            filterName: this._filterText,
            isActive: true,
            isInternalUser: this._isUserInternal,
            userId: user
        }
        return payload;
    }

    private async getSavedFilters(){
        const user = AuthService.getCurrentUserId();
        try {
            const response = await UserConfigService.getSavedFilter(user);
            if(response){
                this._savedFilters = response;
            }
            else
                this._savedFilters = [];
        } catch (e) {
            console.error(e);
        }
    }

    public close(evt?:Event) {
        VoyWeatherBroadcastManager.exitMode();
        this._copyClicked = false;
        this.dispatchEvent(new Event('close'));
        if(this._filterApplied){
            UiStateModel!.setLeftPanelVisibility(true, "hide-anchor");
            UiStateModel!.setShowAnchor(false);
            this._filterApplied = false;
            this._filterText = '';
            UiStateModel!.setValue('q', this.previousFilter);
            this._commands = [];
            this.filterResult = '';
            this.isFilterOpen = false;
        }
        this.previousFilter = '';
        this._onClose = true;
        this._selection.clear()
        this.requestUpdate()
        if (evt) {
            evt.stopPropagation();
        }
        return false;
    }
}
